import React, { useState } from "react";
import api, { FOUNDATION } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("कृपया नाम एवं संदेश भरें।");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("आपका संदेश प्राप्त हो गया। धन्यवाद!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("संदेश भेजने में समस्या हुई। कृपया पुनः प्रयास करें।");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader testId="contact-header" title="संपर्क करें" subtitle="किसी भी जानकारी, सहयोग अथवा सुझाव हेतु हमसे संपर्क करें" />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display text-2xl text-[#0a2540] mb-6">संपर्क जानकारी</h2>
          <div className="space-y-5">
            <div className="flex items-start gap-4 bg-white border border-amber-200/60 rounded-xl p-5">
              <div className="w-11 h-11 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><MapPin size={22} /></div>
              <div>
                <p className="font-semibold text-[#0a2540]">पता</p>
                <p className="text-slate-600 mt-1">{FOUNDATION.address}</p>
              </div>
            </div>
            <a href={`tel:${FOUNDATION.phone.replace(/\s/g, "")}`} className="flex items-start gap-4 bg-white border border-amber-200/60 rounded-xl p-5 hover:border-amber-400 transition-colors" data-testid="contact-phone">
              <div className="w-11 h-11 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><Phone size={22} /></div>
              <div>
                <p className="font-semibold text-[#0a2540]">फोन</p>
                <p className="text-slate-600 mt-1 font-eng">{FOUNDATION.phone}</p>
              </div>
            </a>
            <a href={`mailto:${FOUNDATION.email}`} className="flex items-start gap-4 bg-white border border-amber-200/60 rounded-xl p-5 hover:border-amber-400 transition-colors" data-testid="contact-email">
              <div className="w-11 h-11 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><Mail size={22} /></div>
              <div>
                <p className="font-semibold text-[#0a2540]">ईमेल</p>
                <p className="text-slate-600 mt-1 font-eng">{FOUNDATION.email}</p>
              </div>
            </a>
            <div className="flex gap-3 pt-2">
              <a href={FOUNDATION.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook"
                 className="flex items-center gap-2 bg-[#0a2540] text-white px-4 py-2.5 rounded-lg hover:bg-[#061729] transition-colors">
                <i className="fa-brands fa-facebook-f" /> <span className="font-eng text-sm">{FOUNDATION.facebookName}</span>
              </a>
              <a href={FOUNDATION.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram"
                 className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2.5 rounded-lg hover:bg-amber-700 transition-colors">
                <i className="fa-brands fa-instagram" /> <span className="font-eng text-sm">{FOUNDATION.instagramName}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="bg-white border border-amber-200/60 rounded-xl p-7 shadow-sm">
          <h2 className="font-display text-2xl text-[#0a2540] mb-6">संदेश भेजें</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[#0a2540]">नाम *</Label>
              <Input data-testid="contact-name-input" value={form.name} onChange={set("name")} className="mt-1.5 border-amber-200" placeholder="आपका नाम" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0a2540]">ईमेल</Label>
                <Input data-testid="contact-email-input" value={form.email} onChange={set("email")} className="mt-1.5 border-amber-200" placeholder="email@example.com" />
              </div>
              <div>
                <Label className="text-[#0a2540]">फोन</Label>
                <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5 border-amber-200" placeholder="मोबाइल नंबर" />
              </div>
            </div>
            <div>
              <Label className="text-[#0a2540]">विषय</Label>
              <Input data-testid="contact-subject-input" value={form.subject} onChange={set("subject")} className="mt-1.5 border-amber-200" placeholder="विषय" />
            </div>
            <div>
              <Label className="text-[#0a2540]">संदेश *</Label>
              <Textarea data-testid="contact-message-input" value={form.message} onChange={set("message")} rows={5} className="mt-1.5 border-amber-200" placeholder="अपना संदेश लिखें..." />
            </div>
            <Button type="submit" disabled={submitting} data-testid="contact-submit-button" className="w-full bg-[#0a2540] hover:bg-[#061729] h-11 gap-2">
              <Send size={17} /> {submitting ? "भेजा जा रहा है..." : "संदेश भेजें"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
